import React, { ReactElement, useEffect, useState } from 'react';
import { useTheme } from 'styled-components';

import { useAppContext } from '@prisjakt/ui-app-context';
import { PriceRowContextContainer } from '../../../price-row-context';

import { Clickable } from '@prisjakt/ui/clickable';
import { Popover } from '@prisjakt/ui/popover';
import { Alignment } from '@prisjakt/ui/floater';
import { BodySmallText, H4Text } from '@prisjakt/ui/text/variants';
import { PriceDisplay } from 'src/components/price-list/types';
import { PriceRow } from '..';

import {
  OfferItemExperimentType,
  useNewProductOfferItemExperiment,
} from 'src/hooks/experiments';

import { Brand, Price, ProductPageQuery } from 'src/types';
import { CurrentCampaign } from 'src/areas/product/types';
import { Offer } from '../../types';

import {
  Ribbon,
  IconWrapper,
  InfoIcon,
  PrisjaktIcon,
  withOurChoiceStyles,
  Wrapper,
  TitleWrapper,
} from './styles';

type Props = {
  ourChoice: Price;
  product: ProductPageQuery['product'];
  priceDisplay: PriceDisplay;
  campaign: CurrentCampaign;
  offer: Offer;
  showExpandButton?: boolean;
  id?: string;
};

export const OurChoice: React.FC<Props> = React.memo(
  ({
    ourChoice,
    product,
    priceDisplay,
    campaign,
    offer,
    showExpandButton = true,
    id,
  }) => {
    const { t } = useAppContext();
    const [showPopover, setShowPopover] = useState(false);
    const [
      newOfferItemExperimentState,
      setNewOfferItemExperimentState,
    ] = useState<OfferItemExperimentType>(false);

    const newOfferItemExperiment = useNewProductOfferItemExperiment();

    useEffect(() => {
      setNewOfferItemExperimentState(newOfferItemExperiment);
    }, [newOfferItemExperiment]);

    if (!product) {
      return null;
    }

    return (
      <>
        <TitleWrapper>
          <H4Text style={{ display: 'inline' }}>
            {t('product', 'prices.our-choice')}{' '}
          </H4Text>

          <Clickable
            onClick={() => setShowPopover(state => !state)}
            containerProps={{
              'aria-describedby': 'our-choice-explanation-popover',
              style: { display: 'inline' },
            }}
          >
            <InfoIcon color="base600" size={15} />
          </Clickable>
          <Popover
            id="our-choice-explanation-popover"
            visible={showPopover}
            onClose={e => {
              e?.stopPropagation();
              setShowPopover(false);
            }}
            horizontalAlign={Alignment.Center}
            verticalAlign={Alignment.StartOutside}
            width={300}
          >
            <BodySmallText>
              {t('product', 'prices.our-choice-explanation')}
            </BodySmallText>
          </Popover>
        </TitleWrapper>

        <PriceRowContextContainer
          key={ourChoice.id}
          productId={product.id}
          productName={product.name}
          productImage={product.media.first}
          brand={product.brand as Brand}
          priceDisplay={priceDisplay}
          campaign={campaign}
          showExpandButton={showExpandButton}
          offer={offer}
          position="productHeader"
          isFeatured={Boolean(offer.featured && offer.externalUri)}
          isOurChoice
          forceMobileView
          newOfferItemExperiment={newOfferItemExperimentState}
        >
          <OurChoiceWrapper withShadow>
            <PriceRow
              item={product.prices?.ourChoice as Price}
              index={0}
              key={0}
            />
          </OurChoiceWrapper>
        </PriceRowContextContainer>
      </>
    );
  }
);

export type OurChoiceWrapperProps = {
  children: ReactElement;
  className?: string;
  withShadow?: boolean;
};

export const OurChoiceWrapper = withOurChoiceStyles(
  ({ children, className }) => {
    const { colors } = useTheme();
    if (!children) {
      return null;
    }

    return (
      <Wrapper>
        <Ribbon>
          <IconWrapper>
            <PrisjaktIcon color={colors.positive500} size={22} />
          </IconWrapper>
        </Ribbon>
        {React.cloneElement(children, {
          className,
        })}
      </Wrapper>
    );
  }
);
